import {useMutation} from "react-query";
import Alert from "react-s-alert";
import {AdmissionsService} from "../../../services/AdmissionsService";
import {readResidentNameAnswer} from "../../../models/AdmissionModel.helpers";
import {useAdmissionsRestoreModalContext} from "./context";
import {RestoreAdmissionModal} from "../../restore-admission-modal";
import {
  getWorkflowLabel,
  isPendingWorkflowType,
} from "../../start-workflow/workflowMapping";

type StateProps = Readonly<{
  onRestored?: (admissionId: string) => void;
}>;

export const AdmissionsRestoreAdmissionModal = ({onRestored}: StateProps) => {
  const {admission, toggle} = useAdmissionsRestoreModalContext();

  const {mutate, isLoading} = useMutation(
    ({admissionId}: {admissionId: string}) =>
      AdmissionsService.restore(admissionId),
    {
      onError: () => {
        // "Pending workflow" is stored capitalized (it reads fine as a label), but needs
        // lowercasing mid-sentence; "Admission"/"Arbitration" already read fine as-is.
        const label = getWorkflowLabel(admission?.workflowType);
        const isPending = isPendingWorkflowType(admission?.workflowType);
        const midSentenceLabel = isPending ? label.toLowerCase() : label;
        Alert.error(`Failed to restore the ${midSentenceLabel}`);
      },
      onSuccess: (_data, {admissionId}) => {
        const label = getWorkflowLabel(admission?.workflowType);
        const isPending = isPendingWorkflowType(admission?.workflowType);
        const sentence = isPending ? `The ${label.toLowerCase()}` : label;
        Alert.success(`${sentence} was successfully restored.`);
        onRestored?.(admissionId);
      },
      onSettled: () => {
        toggle();
      },
    },
  );

  return (
    <RestoreAdmissionModal
      isOpen={!!admission}
      outdatedContract={admission?.outdatedContract}
      admissionId={admission?.id}
      admissionCode={admission?.code}
      residentName={readResidentNameAnswer(admission)}
      isLoading={isLoading}
      onCancel={() => mutate({admissionId: admission?.id})}
      onRequestClose={() => toggle()}
      admissionStatus={admission?.status}
      workflowType={admission?.workflowType}
    />
  );
};
